import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Button,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  TABLE_HEAD_SIGNAL_LOCATES,
  findStrategy,
  findFund,
  TABLE_HEAD_ALLOC,
  TABLE_HEAD_SWAP_STATUS,
  TABLE_HEAD_SWAP_STATUS_SUMMARY,
} from "../../../../../../component/Methods";
import SignalsLimitedByLocates from "../../DataEntity/SignalsLimitedByLocates";
import { getSecuritiesWithMarketDataRequest } from "../../../../../../utils/helpers";
import RebalancingInfo from "..";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../../../../../Redux/Reducers/uiSlice";
import { updateSwapLoading } from "../../../../../../Redux/Reducers/rebalancerSlice";
import { toast } from "react-toastify";
import Allocated from "../../DataEntity/Allocated";
import SwapStatus from "../../DataEntity/SwapStatus";
import moment from "moment";
import { uploadToS3 } from "../../../../../../utils/s3Utils";
import {
  getPayloadSize,
  payloadQualifiesForS3Upload,
} from "../../../../../../component/Methods/checks";
import { sendWebSocketMessage } from "../../../../../../hooks/useWebSocket";
import { useRebalancerState } from "../../../../../../hooks/useOptimizedState";


const SleeveRebalance = ({
  setCurrentStep,
  rebalancedFunds,
  setSelectedRebalancedFunds,
  rebalancedStrategies,
  setRebalancedStrategies,
  onRebalanceTypeChange,
  selectedSwapContract,
  setSelectedSwapContract,
  deltaRebalancer,
  setDeltaRebalancer,
  deltaValue,
  setDeltaValue,
  driftValue,
  setDriftValue,
}) => {
  const dispatch = useDispatch();

  const [data, setData] = useState([]);
  const [swapData, setSwapData] = useState([]);
  const [alloc, setAlloc] = useState([]);
  const [selectedValue, setSelectedValue] = useState("specific");
  
  // Notify parent when selection changes
  useEffect(() => {
    if (selectedValue === "swap") {
      onRebalanceTypeChange("swap");
    } else if (selectedValue === "all") {
      onRebalanceTypeChange("all");
    } else {
      onRebalanceTypeChange("specific");
    }
  }, [selectedValue, onRebalanceTypeChange]);
  const [selectedFundsId, setSelectedFundsId] = useState(null);
  const [selectedFunds, setSelectedFunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSignalsTable, setShowSignalsTable] = useState(false);
  const [strategyValObj, setstrategyValObj] = useState([]);
  const [error, setError] = useState("");
  const [marketDataFetched, setMarketDataFetched] = useState(false);
  const [swapDataReady, setSwapDataReady] = useState(false);
  const [hasClickedCheckButton, setHasClickedCheckButton] = useState(false);
  const { livePrices, markets, isStrategist, isInvestor, lots, accountingData, funds, strategies, swapStrategiesAvailableDate, 
    managers, swapLoading, swapStrategies } = useRebalancerState();
  const { userInfo, apiInfo } = useSelector((state) => state?.users);

  const myFunds = accountingData[0]?.funds;
  const FundList = (funds?.map((i) => i?.abbreviation) || [])?.sort();
  const smaList = Object.keys(managers[0]?.smas || {})?.map(key => managers[0]?.smas[key]?.abbreviation)?.filter(Boolean)?.sort();
  const StrategyList = (strategies?.map((i) => i?.name) || [])?.sort();
  const swapContracts = (
    strategies?.filter((i) => i?.name?.toLowerCase().endsWith("x")) || []
  )?.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  useEffect(() => {}, [livePrices]);

  const sendMarketDataRequest = async (request) => {
    const socketRequest = {
      action: "market_data",
      data: request,
    };

    const payloadSize = getPayloadSize(request);
    const qualifiesForS3 = payloadQualifiesForS3Upload(payloadSize);

    if (qualifiesForS3) {
      try {
        const s3Result = await uploadToS3(
          socketRequest,
          apiInfo,
          userInfo,
          "market_data"
        );

        if (s3Result.success) {
          const s3ReferencePayload = {
            action: "market_data",
            file: s3Result.fileName,
          };

          dispatch(addActiveRequests("market_data"));
          sendWebSocketMessage(JSON.stringify(s3ReferencePayload));
        } else {
          toast.error("Failed to upload to S3. Please try again later.");
        }
      } catch (error) {
        toast.error("Failed to upload to S3. Please try again later.");
      }
    } else {
      dispatch(addActiveRequests("market_data"));
      sendWebSocketMessage(JSON.stringify(socketRequest));
    }
  };

  const handleFetchData = async () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setShowSignalsTable(true);
      setMarketDataFetched(true);
    }, 5000);

    if (rebalancedStrategies?.length > 0) {
      const data = getSecuritiesWithMarketDataRequest(
        lots,
        selectedFundsId,
        rebalancedStrategies,
        strategies,
        livePrices
      );
      const tickerMap = new Map();

      data?.securities?.forEach((security) => {
        const symbol = security?.ticker?.stock ? `${security?.ticker?.symbol?.toLowerCase()}/${security?.ticker?.stock?.toLowerCase()}` : security?.ticker?.symbol?.toLowerCase();

        if (!tickerMap?.has(symbol)) {
          tickerMap?.set(symbol, security);
        } else {
        }
      });

      const uniqueSecurities = Array.from(tickerMap?.values());

      setData(uniqueSecurities);

      if (data?.market_data_request && data.market_data_request.length > 0) {
        for (const request of data.market_data_request) {
          await sendMarketDataRequest(request);
        }
      }
    }
  };

  const setStrategiesToggle = (newValue) => {
    const enrichedStrategyData = newValue?.map((item) => {
      const fullStrategy = strategies?.find(
        (strategy) => strategy.name === item
      );

      return fullStrategy.strategyId;
    });
    setRebalancedStrategies(enrichedStrategyData);
  };

  const handleCheckSwapAvailability = () => {
    setSwapDataReady(false);
    setHasClickedCheckButton(true);
    if (selectedSwapContract?.length > 0) {
      const data = {
        strategyIds: selectedSwapContract,
        step: 1,
      };

      const socketRequest = {
        action: "rebalance_swaps",
        data: data,
      };
      dispatch(addActiveRequests("rebalance_swaps"));
      dispatch(updateSwapLoading(true));
      sendWebSocketMessage(JSON.stringify(socketRequest));
    }
  };

  const resetToBack = () => {
    setShowSignalsTable(false);
    setSelectedRebalancedFunds([]);
    setSelectedFundsId(null);
    setSelectedFunds([]);
    setstrategyValObj([]);
    setRebalancedStrategies([]);
    setError("");
    setMarketDataFetched(false);
    setData([]);
    setAlloc([]);
    setSelectedSwapContract([]);
  };
  const onNext = () => {
    if (selectedValue === "swap") {
      onRebalanceTypeChange("swap");
      if (selectedSwapContract?.length > 0) {
        const data = {
          strategyIds: selectedSwapContract,
          step: 2,
        };

        const socketRequest = {
          action: "rebalance_swaps",
          data: data,
        };

        sendWebSocketMessage(JSON.stringify(socketRequest));
        dispatch(addActiveRequests("rebalance_swaps"));
        dispatch(updateLoading(true));
        dispatch(updateLoadingMsg("Checking Rebalancer Items"));
      }
      setCurrentStep((prev) => prev + 1);
    } else if (selectedValue === "all") {
      // For "all" type, send create_rebalancer_items request
      onRebalanceTypeChange("all");
      const socketRequest = {
        action: "create_rebalancer_items",
        data: {
          fundIds: selectedFundsId,
          blacklistedTickers: {}, // Empty blacklist for "all" type
          rebalancerType: 1, // 1 for "all"
          deltaRebalancer: Number(deltaRebalancer),
          deltaValue: Number(deltaValue),
          strategies: rebalancedStrategies,
          driftValue: Number(driftValue),
        },
      };
      sendWebSocketMessage(JSON.stringify(socketRequest));
      dispatch(addActiveRequests("create_rebalancer_items"));
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("Creating Rebalancer Items"));
      setCurrentStep((prev) => prev + 2);
    } else {
      // For "specific" type, send rebalancer_check_blacklist request
      onRebalanceTypeChange("specific");
      const socketRequest = {
        action: "rebalancer_check_blacklist",
        data: {
          fundIds: selectedFundsId,
          rebalancerType: 0, // 0 for "specific"
          strategies: rebalancedStrategies,
        },
      };
      sendWebSocketMessage(JSON.stringify(socketRequest));
      dispatch(addActiveRequests("rebalancer_check_blacklist"));
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("Checking Blacklist"));
      setCurrentStep((prev) => prev + 1);
    }
  };
  const handleSelectedFundsChange = (event, newValue, isAll) => {
    setSelectedFunds(newValue);
    const fundIds = newValue
      ?.map((fundAbbreviation) => {
        const fund = funds?.find((f) => f.abbreviation === fundAbbreviation);
        return fund?.fundId;
      })
      .filter((fundId) => fundId !== undefined);
    if (isAll) {
      const strategyIds = newValue
        ?.map((fundAbbreviation) => {
          const fundFromFunds = funds?.find(
            (f) => f.abbreviation === fundAbbreviation
          );
          const fundId = fundFromFunds?.fundId;
          const Fund = funds?.find((f) => f.fundId === fundId);
          const allocationItems = Fund?.allocation?.items;

          if (allocationItems) {
            return Object.keys(allocationItems).filter((strategyId) => {
              const strategyData = allocationItems[strategyId];
              return strategyData?.totalAllocation >= 0;
            });
          }
          return [];
        })
        .flat()
        .filter((strategyId) => strategyId !== undefined);
      const uniqueStrategyIds = [...new Set(strategyIds)];

      setRebalancedStrategies(uniqueStrategyIds);
    } else {
      setRebalancedStrategies([]);
    }
    setSelectedFundsId(fundIds);
    setSelectedRebalancedFunds(fundIds);
  };
  useEffect(() => {
    if (!selectedFunds || selectedFunds.length === 0) {
      setMarketDataFetched(false);
      setstrategyValObj([]);
      setRebalancedStrategies([]);
      setShowSignalsTable(false);
      setData([]);
      setAlloc([]);
    }
  }, [selectedFunds]);

  useEffect(() => {
    if (!swapLoading && selectedSwapContract?.length > 0) {
      setSwapDataReady(true);
    }
  }, [swapLoading, selectedSwapContract]);
  const GetAllocation = (selectedFundsId, rebalancedStrategies) => {
    let errorFound = false;
    let updatedAlloc = [];

    selectedFundsId?.forEach((fundId) => {
      const fundData = funds?.find((f) => f.fundId === fundId);

      if (fundData?.allocation?.items) {
        rebalancedStrategies?.forEach((strategy) => {
          const strategyData = fundData?.allocation?.items[strategy];

          if (!strategyData) {
            errorFound = true;

            updatedAlloc.push({
              fund: findFund(fundId, funds),
              strategy: findStrategy(strategy, strategies),
              status: "Failed",
              reason: `${findStrategy(
                strategy,
                strategies
              )} not found in fund ${findFund(fundId, funds)}`,
            });
          } else {
            const requiredFields = [
              "account",
              "broker",
              "percentLong",
              "percentShort",
              "totalAllocation",
            ];

            const missingFields = requiredFields.filter(
              (field) =>
                strategyData[field] === undefined ||
                strategyData[field] === null
            );

            if (missingFields?.length > 0) {
              errorFound = true;
              updatedAlloc.push({
                fund: findFund(fundId, funds),
                strategy: findStrategy(strategy, strategies),
                status: "Failed",
                reason: `${findStrategy(strategy, strategies)} ${findFund(
                  fundId,
                  funds
                )} has missing fields: ${missingFields.join(", ")}`,
              });
            }
          }
        });
      } else {
        errorFound = true;
        updatedAlloc.push({
          fund: findFund(fundId, funds),
          strategy: "N/A",
          status: "Failed",
          reason: `Allocation items not found for fund ${findFund(
            fundId,
            funds
          )}`,
        });
      }
    });

    if (errorFound) {
      setError(
        "Allocation is not set correctly for the listed funds and strategies"
      );
    } else {
      setError("");
    }

    setAlloc(updatedAlloc);
  };

  useEffect(() => {
    if (
      managers &&
      managers.length > 0 &&
      selectedFundsId &&
      selectedFundsId.length > 0 &&
      rebalancedStrategies &&
      rebalancedStrategies.length > 0 &&
      selectedValue !== "all"
    ) {
      GetAllocation(selectedFundsId, rebalancedStrategies);
    }
  }, [managers, selectedFunds, selectedFundsId, rebalancedStrategies]);

  const handleRadioChange = (event) => {
    const newValue = event.target.value;
    setSelectedValue(newValue);
    resetToBack();
  };

  return (
    <Container disableGutters maxWidth="100%" sx={{ marginTop: 2 }}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={4}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              height: "100%",
              padding: "10px",
            }}
          >
            <FormControl component="fieldset">
              <RadioGroup
                value={selectedValue}
                onChange={handleRadioChange}
                name="radio-buttons-group"
                column
              >
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label="SMA Rebalancer"
                  onClick={resetToBack}
                />
                {selectedValue === "all" && (
                  <Grid item xs={12}>
                    <FormControl sx={{  my: 1, width: "100%" }}>
                      <Autocomplete
                        multiple
                        options={smaList}
                        getOptionLabel={(option) => option}
                        onChange={(event, newValue) =>
                          handleSelectedFundsChange(event, newValue, true)
                        }
                        disableCloseOnSelect
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select SMA to Rebalance"
                            variant="outlined"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                )}
                <FormControlLabel
                  value="specific"
                  control={<Radio />}
                  label="Sleeve Rebalancer"
                  onClick={resetToBack}
                />
                {selectedValue === "specific" && (
                  <Grid item xs={12}>
                    <div
                      style={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <FormControl sx={{ mt: 1, mb: 2, width: "100%" }}>
                        <Autocomplete
                          multiple
                          options={FundList}
                          getOptionLabel={(option) => option}
                          onChange={(event, newValue) =>
                            handleSelectedFundsChange(event, newValue, false)
                          }
                          disableCloseOnSelect
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select Fund"
                              variant="outlined"
                              fullWidth
                            />
                          )}
                        />
                      </FormControl>
                    </div>
                  </Grid>
                )}
                {selectedFunds &&
                  selectedFunds.length > 0 &&
                  selectedValue === "specific" && (
                    <Grid item xs={12}>
                      <div
                        style={{ display: "flex", justifyContent: "space-between" }}
                      >
                        <FormControl sx={{ mb: 1, width: "100%" }}>
                          <Autocomplete
                            multiple
                            options={StrategyList}
                            onChange={(e, newValue) => {
                              setStrategiesToggle(newValue);
                              setstrategyValObj(newValue);
                              setShowSignalsTable(false);
                              setMarketDataFetched(false);
                              setData([]);
                            }}
                            getOptionLabel={(option) => option}
                            disableCloseOnSelect
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Strategies"
                                variant="outlined"
                                fullWidth
                              />
                            )}
                          />
                        </FormControl>
                      </div>
                    </Grid>
                  )}
                <FormControlLabel
                  value="swap"
                  control={<Radio />}
                  label="Swap Rebalancer"
                  onClick={resetToBack}
                />
                {selectedValue === "swap" && (
                  <Grid item xs={12}>
                    <FormControl sx={{  my: 3, width: "100%" }}>
                      <Autocomplete
                        multiple
                        options={swapContracts}
                        getOptionLabel={(option) => `${option.name}`}
                        onChange={(event, newValue) => {
                          const selectedIds = newValue.map(
                            (strategy) => strategy.strategyId
                          );
                          setSelectedSwapContract(selectedIds);
                          setSwapData(newValue);
                          setSwapDataReady(false); // Reset when strategy selection changes
                          setHasClickedCheckButton(false); // Reset button click state
                        }}
                        disableCloseOnSelect
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Contracts"
                            variant="outlined"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                )}
              </RadioGroup>
            </FormControl>

            {selectedFunds && selectedValue !== "swap" && (
              <Grid item xs={12}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  <RebalancingInfo
                    funds={rebalancedFunds}
                    rebalanceType={selectedValue}
                    deltaRebalancer={deltaRebalancer}
                    setDeltaRebalancer={setDeltaRebalancer}
                    deltaValue={deltaValue}
                    setDeltaValue={setDeltaValue}
                    driftValue={driftValue}
                    setDriftValue={setDriftValue}
                  />
                </div>
              </Grid>
            )}

            {selectedFunds &&
              selectedFunds.length > 0 &&
              selectedValue !== "swap" &&
              (selectedValue === "all" ||
                (selectedValue === "specific" &&
                  strategyValObj?.length > 0)) && (
                <Grid item xs={12}>
                  <FormControl sx={{ width: "75%", mb: 4 }}>
                    <Button
                      variant="contained"
                      onClick={handleFetchData}
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress size={24} />
                      ) : (
                        "Fetch Securities & Signals"
                      )}
                    </Button>
                  </FormControl>
                </Grid>
              )}

            {selectedValue !== "swap" &&
              ((selectedValue === "all" && showSignalsTable) ||
                (selectedValue === "specific" &&
                  showSignalsTable &&
                  strategyValObj?.length > 0)) && (
                <Grid item xs={12}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <SignalsLimitedByLocates
                      markets={markets}
                      dummy={data}
                      addCol={true}
                      tableHeader={TABLE_HEAD_SIGNAL_LOCATES}
                      marketData={livePrices}
                      showPopup={true}
                      strategyListToggle={rebalancedStrategies}
                      isStrategist={isStrategist}
                      isInvestor={isInvestor}
                    />
                  </div>
                </Grid>
              )}

            {selectedValue === "swap" && selectedSwapContract?.length > 0 && (
              <Grid item xs={12}>
                <FormControl
                  sx={{ width: "75%",  marginTop: 0 }}
                >
                  <Button
                    variant="contained"
                    onClick={handleCheckSwapAvailability}
                    disabled={swapLoading}
                  >
                    {swapLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      "CHECK REBALANCE AVAILABILITY"
                    )}
                  </Button>
                </FormControl>
              </Grid>
            )}

            {(() => {
              const shouldShow =
                selectedValue === "swap" &&
                swapStrategies?.length > 0 &&
                selectedSwapContract?.length > 0 &&
                swapDataReady &&
                !swapLoading &&
                hasClickedCheckButton;

              return shouldShow;
            })() && (
              <Grid item xs={12}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <SwapStatus
                    swapStrategies={swapStrategies}
                    tableHeader={TABLE_HEAD_SWAP_STATUS_SUMMARY}
                  />
                </div>
              </Grid>
            )}
          </div>
        </Grid>

        <Grid item xs={12} md={6} sx={{ mt: 2 }}>
          {strategyValObj?.length > 0 && selectedValue !== "swap" && (
            <Box
              sx={{ mb: 2, p: 2, bgcolor: "background.paper", borderRadius: 1 }}
            >
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, fontWeight: "bold" }}
              >
                Signal Updated:
              </Typography>
              <Grid container spacing={1}>
                {strategyValObj?.map((strategyName) => {
                  const strategy = strategies.find(
                    (s) => s.name === strategyName
                  );
                  return (
                    <Grid item xs={12} key={strategy?.strategyId}>
                      <Typography variant="body2">
                        {strategyName}:{" "}
                        {strategy?.signals?.date
                          ? moment(strategy?.signals?.date).format(
                              "Do MMMM YYYY - h:mm A"
                            )
                          : "No date available"}
                      </Typography>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
          {alloc && alloc?.length > 0 && selectedValue !== "swap" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
              }}
            >
              <Box sx={{ width: "100%", height: "100%" }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <Allocated
                    alloc={alloc}
                    tableHeader={TABLE_HEAD_ALLOC}
                    error={error}
                  />
                </div>
              </Box>
            </div>
          )}
        </Grid>

        <Grid item xs={12} md={2}>
          {((selectedFunds &&
            selectedValue !== "swap" &&
            (selectedValue === "all" ||
              (selectedValue === "specific" && strategyValObj?.length > 0)) &&
            marketDataFetched) ||
            (selectedValue === "swap" &&
              selectedSwapContract?.length > 0 &&
              swapStrategies?.length > 0 &&
              swapDataReady &&
              !swapLoading &&
              hasClickedCheckButton)) && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={onNext}
                disabled={
                  selectedValue === "swap" &&
                  swapStrategies?.some(
                    (strategy) => strategy.available === false
                  )
                }
                sx={{ width: "150px", height: "40px" }}
              >
                Next
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default SleeveRebalance;
